import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import { CATEGORIES } from '../../utils/categoryConstants';

const AddServiceModal = ({ open, onClose, onServiceAdded }) => {
  const { selectedGarage } = useGarage();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    price: '',
    category: '',
    isAddon: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Use consistent categories from categoryConstants
  const serviceCategories = CATEGORIES;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }
    
    if (!formData.duration) {
      newErrors.duration = 'Duration is required';
    } else if (isNaN(formData.duration) || formData.duration <= 0) {
      newErrors.duration = 'Duration must be a positive number';
    }
    
    if (!formData.price) {
      newErrors.price = 'Price is required';
    } else if (isNaN(formData.price) || formData.price < 0) {
      newErrors.price = 'Price must be a valid number';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const serviceData = {
        garage_id: selectedGarage?.id,
        name: formData.name.trim(),
        description: formData.description.trim(),
        duration_minutes: parseInt(formData.duration),
        price: parseFloat(formData.price),
        category: formData.category,
        is_addon: formData.isAddon
      };

      const response = await apiService.createService(serviceData);
      
      if (onServiceAdded) {
        onServiceAdded(response.data);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error creating service:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to create service' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      duration: '',
      price: '',
      category: '',
      isAddon: false
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="span" fontWeight="600">
          Add Service
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Service Name */}
          <TextField
            fullWidth
            label="Service Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g., Full MOT Test"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Service description (optional)"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          {/* Duration and Price Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Duration"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              error={!!errors.duration}
              helperText={errors.duration}
              placeholder="60"
              InputProps={{
                endAdornment: <InputAdornment position="end">minutes</InputAdornment>
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />

            <TextField
              label="Price"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              error={!!errors.price}
              helperText={errors.price}
              placeholder="54.85"
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Category */}
          <FormControl 
            fullWidth 
            error={!!errors.category}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          >
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={(e) => handleInputChange('category', e.target.value)}
            >
              {serviceCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {errors.category}
              </Typography>
            )}
          </FormControl>

          {/* Error Message */}
          {errors.submit && (
            <Typography variant="body2" color="error" textAlign="center">
              {errors.submit}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddServiceModal;